<script>
import paper from 'paper';
import { mapMutations } from 'vuex';
import tool from '../mixins/tool';
import UndoAction from '../undo';

import { invertColor } from '../libs/colors';
import iconImg from '../images/polgon-1.png';
import iconImgActive from '../images/polgon-active.png';

export default {
    name: 'PolygonTool',
    mixins: [tool],
    props: {
        scale: {
            type: Number,
            default: 1,
        },
        settings: {
            type: [Object, null],
            default: null,
        },
        // 是否允许绘制，编辑
        enable: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            toolTipText: {
                // 工具提示
                zh: '多边形绘制工具\n', // 中文
                en: 'Polygon tool', // 英文
                finish_zh: 'shift+右键结束绘制\n',
                finish_en: "shift+'right click' to finish\n",
            },
            iconImage: iconImg,
            iconImageActive: iconImgActive,
            icon: 'fa-pencil',
            name: 'Polygon',
            scaleFactor: 3,
            cursor: 'crosshair',
            polygon: {
                // completeDistance: 5,
                completeDistance: 10,
                minDistance: 2,
                path: null,
                guidance: true,
                simplify: 1,
                pathOptions: {
                    strokeColor: 'black',
                    strokeWidth: 1,
                },
            },
            color: {
                blackOrWhite: true,
                auto: true,
                radius: 10,
                circle: null,
            },
            actionTypes: Object.freeze({
                ADD_POINTS: 'Added Points',
                CLOSED_POLYGON: 'Closed Polygon',
                DELETE_POLYGON: 'Delete Polygon',
            }),
            actionPoints: 0,
            mouseDownTime: '',
            mouseDownTimeDelta: 300,
        };
    },
    methods: {
        ...mapMutations('annotator', ['addUndo', 'removeUndos']),
        export() {
            return {
                guidance: this.polygon.guidance,
                completeDistance: this.polygon.completeDistance,
                minDistance: this.polygon.minDistance,
                blackOrWhite: this.color.blackOrWhite,
                auto: this.color.auto,
                radius: this.color.radius,
            };
        },
        /**
         * 关闭右键菜单
         *
         */
        closeContextmenu() {
            this.contextmenuVisible = false;
        },
        // 设置首选项
        setPreferences(pref) {
            this.polygon.guidance = pref.guidance || this.polygon.guidance;
            this.polygon.completeDistance = pref.completeDistance || this.polygon.completeDistance;
            this.polygon.minDistance = pref.minDistance || this.polygon.minDistance;
            this.color.blackOrWhite = pref.blackOrWhite || this.color.blackOrWhite;
            this.color.auto = pref.auto || this.color.auto;
            this.color.radius = pref.radius || this.color.radius;
        },
        /**
         * Creates a new selection polygon path
         * 加载多边形
         */
        createPolygon() {
            if (this.color.auto) {
                this.color.circle = new paper.Path.Circle(new paper.Point(0, 0), this.color.radius);
            }
            this.polygon.path = new paper.Path(this.polygon.pathOptions);
        },
        /**
         * Frees current polygon
         * 删除多边形
         */
        deletePolygon() {
            if (this.polygon.path == null) return;

            this.polygon.path.remove();
            this.polygon.path = null;

            if (this.color.circle == null) return;
            this.color.circle.remove();
            this.color.circle = null;
        },
        // 自动笔划颜色
        autoStrokeColor(point) {
            if (this.color.circle == null) return;
            if (this.polygon.path == null) return;
            if (!this.color.auto) return;

            this.color.circle.position = point;
            let raster = this.$parent.image.raster;
            let color = raster.getAverageColor(this.color.circle);
            if (color) {
                this.polygon.pathOptions.strokeColor = invertColor(
                    color.toCSS(true),
                    this.color.blackOrWhite,
                );
            }
        },
        // 鼠标拖动时
        onMouseDrag(event) {
            if (!this.enable) return;
            if (this.polygon.path == null) return;
            this.actionPoints++;
            this.autoStrokeColor(event.point);
            this.polygon.path.add(event.point);
            this.autoComplete(30);
        },
        // 鼠标放下时
        onMouseDown(event) {
            if (!this.enable) return;
            const mEvent = event.event;
            const button = mEvent.button; // button:0 左键 button:1 鼠标中键 button:2鼠标右键
            // 双击左键结束绘画
            if (this.mouseDownTime) {
                // 通过2次点击的时间间隔是否超过300毫秒来判断是否是双击
                let now = new Date().getTime();
                if (now - this.mouseDownTime < this.mouseDownTimeDelta && button === 0) {
                    // 超过3个点自动结束绘制
                    if (this.polygon.path == null) return;
                    if (this.polygon.path.segments.length < 3) {
                        this.polygon.path.remove();
                        this.polygon.path = null;
                        if (this.color.circle) {
                            this.color.circle.remove();
                            this.color.circle = null;
                        }

                        this.removeUndos(this.actionTypes.ADD_POINTS);
                        return;
                    }
                    this.complete();
                    return;
                }
                this.mouseDownTime = now;
            } else {
                this.mouseDownTime = new Date().getTime();
            }

            const shiftKey = mEvent.shiftKey;
            if (button === 0) {
                // 左键事件
                let wasNull = false;
                if (this.polygon.path == null) {
                    wasNull = true;
                    this.createPolygon();
                }

                this.actionPoints = 1;
                this.polygon.path.add(event.point);
                if (this.autoComplete(3)) return;
                if (this.polygon.guidance && wasNull) this.polygon.path.add(event.point);
            } else if (button === 2) {
                // 右键事件
                if (!shiftKey) {
                    this.removeLastPoint();
                } else {
                    // shift+右键超过3个点自动结束绘制
                    if (this.polygon.path == null) return;
                    if (this.polygon.path.segments.length < 3) {
                        this.polygon.path.remove();
                        this.polygon.path = null;
                        if (this.color.circle) {
                            this.color.circle.remove();
                            this.color.circle = null;
                        }
                        this.removeUndos(this.actionTypes.ADD_POINTS);
                        return;
                    }
                    this.complete();
                }
            }
        },
        // 鼠标向上时
        onMouseUp(e) {
            if (!this.enable) return;
            if (this.polygon.path == null) return;
            let action = new UndoAction({
                name: this.name,
                action: this.actionTypes.ADD_POINTS,
                func: this.undoPoints,
                args: {
                    points: this.actionPoints,
                },
            });

            this.addUndo(action);
        },
        // 鼠标移动时
        onMouseMove(event) {
            if (!this.enable) return;
            if (this.polygon.path == null) return;
            if (this.polygon.path.segments.length === 0) return;
            this.autoStrokeColor(event.point);

            if (!this.polygon.guidance) return;
            this.removeLastPoint();
            this.polygon.path.add(event.point);
        },
        /**
         * Undo points
         * 撤消点
         */
        undoPoints(args) {
            if (this.polygon.path == null) return;

            let points = args.points;
            let length = this.polygon.path.segments.length;

            if (this.polygon.guidance) {
                length -= 1;
            }

            this.polygon.path.removeSegments(length - points, length);
        },
        /**
         * Completes polygon if point being added is close to first point
         * 如果添加的点接近第一个点，则完成多边形
         * @returns {boolean} sucessfully closes object
         */
        autoComplete(minCompleteLength) {
            if (this.polygon.path == null) return false;
            if (this.polygon.path.segments.length < minCompleteLength) return false;

            let last = this.polygon.path.lastSegment.point;
            let first = this.polygon.path.firstSegment.point;

            let completeDistance = this.polygon.completeDistance;
            if (last.isClose(first, completeDistance)) {
                return this.complete();
            }

            return false;
        },
        /**
         * Closes current polygon and unites it with current annotaiton.
         * 关闭当前多边形并将其与当前公告合并
         * @returns {boolean} sucessfully closes object
         */
        complete() {
            if (this.polygon.path == null) return false;

            this.removeLastPoint();

            this.polygon.path.fillColor = 'black';
            this.polygon.path.closePath();

            // this.$parent.uniteCurrentAnnotation(this.polygon.path);

            this.polygon.path.remove();
            this.polygon.path = null;
            if (this.color.circle) {
                this.color.circle.remove();
                this.color.circle = null;
            }

            this.removeUndos(this.actionTypes.ADD_POINTS);
            this.mouseDownTime = '';
            return true;
        },
        removeLastPoint() {
            if (!this.polygon.path) return;
            this.polygon.path.removeSegment(this.polygon.path.segments.length - 1);
        },
    },
    computed: {
        isDisabled() {
            console.log('!this.enable', !this.enable);

            if (!this.enable) return true;
            let hasPath = this.currentAnnotatorHashPath;
            return (
                (this.$parent.current.annotation === -1 && this.$parent.current.category === -1) || hasPath
            );
        },
    },
    watch: {
        isActive(active) {
            if (active) {
                this.tool.activate();
                localStorage.setItem('editorTool', this.name);
            }
        },
        /**
         * Change width of stroke based on zoom of image
         */
        scale(newScale) {
            this.polygon.pathOptions.strokeWidth = newScale * this.scaleFactor;
            if (this.polygon.path != null) {
                this.polygon.path.strokeWidth = newScale * this.scaleFactor;
            }
        },
        /**
         * Remove last point (point were mouse was) if enable guidane
         */
        'polygon.guidance': function (guidance) {
            if (this.polygon.path == null) return;

            if (!guidance && this.polygon.path.length > 1) {
                this.removeLastPoint();
            }
        },
        'polygon.minDistance': function (newDistance) {
            this.tool.minDistance = newDistance;
        },
        'polygon.pathOptions.strokeColor': function (newColor) {
            if (this.polygon.path == null) return;

            this.polygon.path.strokeColor = newColor;
        },
        'color.auto': function (value) {
            if (value && this.polygon.path) {
                this.color.circle = new paper.Path.Circle(new paper.Point(0, 0), this.color.radius);
            }
            if (!value && this.color.circle) {
                this.color.circle.remove();
                this.color.circle = null;
            }
        },
    },
    created() {},
    mounted() {
        this.tool.minDistance = this.minDistance;
    },
};
</script>
